import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { Reveal } from './Reveal';
import wordmark from '@/assets/landing/refify-wordmark.png.asset.json';
import heroMascot from '@/assets/landing/hero-mascot.png.asset.json';

interface HeroSectionProps {
  scrollToSection: (id: string) => void;
}

export function HeroSection({ scrollToSection }: HeroSectionProps) {
  return (
    <section className="relative overflow-hidden bg-landing-sage">
      {/* Giant soft circle anchored to the right viewport edge — half of it sits
          off-screen so only a smooth arc enters the hero, exactly like the Figma
          design (ellipse starting at ~60% of the frame width). Clipped by the
          section's overflow-hidden. */}
      <div
        aria-hidden
        className="hidden lg:block absolute top-1/2 right-0 -translate-y-1/2 translate-x-[28%] w-[58vw] aspect-square rounded-full bg-white/75 pointer-events-none"
      />
      <div className="container mx-auto px-6 pt-28 pb-20 md:pt-36 md:pb-28 lg:min-h-[min(44vw,848px)] relative">
        {/* Paper mascot, right side (desktop only) — anchored to the right edge of the
            centered content column, not the viewport corner, so it follows the text
            as the screen grows wider. Sized relative to hero height like Figma. */}
        <div className="hidden lg:flex absolute inset-y-0 right-[2%] items-center pointer-events-none">
          <Reveal variant="slide-left" delay={500} duration={900} className="relative h-full flex items-center justify-center">
            <img
              src={heroMascot.url}
              alt=""
              className="relative z-10 max-h-[78%] w-auto max-w-[min(31vw,590px)]"
              width={590}
              height={674}
            />
          </Reveal>
        </div>

        {/* Paper mascot, centered below the content on mobile and tablet — the mascot
            is part of the flow here so it never overlaps the text or buttons. */}
        <div className="lg:hidden flex justify-center mt-10 md:mt-12 pointer-events-none">
          <Reveal variant="fade-up" delay={500} duration={900} className="relative">
            <img
              src={heroMascot.url}
              alt=""
              className="relative z-10 w-auto max-w-[200px] sm:max-w-[260px] md:max-w-[320px]"
              width={590}
              height={674}
            />
          </Reveal>
        </div>


        <div className="max-w-3xl mx-auto text-center flex flex-col items-center lg:pr-[min(28vw,420px)] lg:max-w-none">
          <Reveal variant="scale" duration={900}>
            <img
              src={wordmark.url}
              alt="Refify"
              className="h-28 md:h-40 lg:h-48 w-auto"
              width={563}
              height={309}
            />
          </Reveal>

          <Reveal variant="fade-up" delay={140} duration={800}>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-landing-bright leading-tight mt-6 md:mt-8">
              Effortless citing
            </h1>
          </Reveal>

          <Reveal variant="fade-up" delay={320} duration={700}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link
                to="/auth"
                className="inline-flex items-center justify-center h-14 px-10 rounded-xl bg-landing-green text-white text-lg font-semibold shadow-[0px_4px_12px_-3px_rgb(44_150_115_/_0.3)] transition-transform duration-300 hover:-translate-y-0.5"
              >
                Get started!
              </Link>
              <button
                type="button"
                onClick={() => scrollToSection('features')}
                className="inline-flex items-center justify-center gap-2.5 h-14 px-10 rounded-xl border-2 border-landing-line text-landing-line text-lg font-semibold shadow-[0px_2px_4px_-1px_rgb(43_59_54_/_0.06)] transition-transform duration-300 hover:-translate-y-0.5"
              >
                <Play className="w-4 h-4" /> Explore features
              </button>
            </div>
          </Reveal>
          <Reveal variant="fade" delay={480}>
            <p className="text-xs text-landing-green mt-5">
              By signing up, you agree to the{' '}
              <Link to="/terms" className="underline hover:opacity-70 transition-opacity">
                Terms and Conditions
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="underline hover:opacity-70 transition-opacity">
                Privacy Policy
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
